import React from 'react'
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom'

import { LoginPage } from './components/LoginPage'
import { HandoffPage } from './components/HandoffPage'
import { MainPage } from './components/MainPage'
import { SettingsPage } from './components/SettingsPage'
import { LabSessionPage } from './components/session/LabSessionPage'
import { LabModulesPage } from './components/LabModulesPage'
import { ExercisesPage } from './components/ExercisesPage'
import { Layout } from './components/Layout'
import { UserProvider, useUser } from './context/UserContext'
import { Dashboard } from './pages/Dashboard'
import { LabSessionsPage } from './pages/LabSessionsPage'
import { BreadcrumbProvider } from './context/BreadcrumbContext'
import { ReportIssue } from './components/ReportIssue'

function AppRoutes() {
  const { isAuthenticated, isLoading } = useUser()

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/handoff" element={<HandoffPage />} />

      {/* Routes with shared Layout (Header + Sidebar) */}
      <Route element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/main" element={<MainPage />} />
        <Route path="/sessions" element={<LabSessionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/course/:courseId/modules" element={<LabModulesPage />} />
        <Route path="/course/:courseId/module/:moduleId/exercises" element={<ExercisesPage />} />
      </Route>

      {/* Standalone lab session routes (manage their own layout) */}
      {isAuthenticated ? (
        <>
          <Route path="/exercise/:exerciseId/chat" element={<LabSessionPage />} />
          <Route path="/lab/:labId" element={<LabSessionPage />} />
        </>
      ) : (
        <>
          <Route path="/exercise/:exerciseId/chat" element={<Navigate to="/login" />} />
          <Route path="/lab/:labId" element={<Navigate to="/login" />} />
        </>
      )}

      <Route path="/courses" element={<Navigate to="/main" />} />
      <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />
    </Routes>
  )
}

function App(): React.JSX.Element {
  return (
    <UserProvider>
      <HashRouter>
        <BreadcrumbProvider>
          <AppRoutes />
          <ReportIssue />
        </BreadcrumbProvider>
      </HashRouter>
    </UserProvider>
  )
}

export default App